import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, MapPin, Clock, TrendingUp, Save, X, Upload, MessageCircle, User } from 'lucide-react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../firebase';
import { uploadImage, generateImagePath } from '../services/imageUploadService';
import { getPromptsFromFirestore } from '../services/promptsService';
import MapCoordinatePicker from '../components/MapCoordinatePicker';
import ExperienceMediaManager from '../components/ExperienceMediaManager';
import { generateQuickReplies } from '../services/quickRepliesService';

const isDev = Boolean((import.meta as any).env?.DEV);
const logDebug = (...args: any[]) => {
  if (isDev) console.log(...args);
};

interface MediaItem {
  id: string;
  url: string;
  title: string;
  description?: string;
  type: 'image' | 'podcast' | 'video';
  uploadedAt?: string;
}

interface QuickReply {
  id: string;
  text: string;
  icon?: string;
}

interface Experience {
  id: string;
  title: string;
  location: string;
  description: string;
  image: string;
  geniusLociId?: string;
  promptId?: string;
  tags: Array<{
    id: string;
    name: string;
    icon: string;
    color: string;
  }>;
  category: string;
  duration: string;
  difficulty: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  media?: {
    images?: MediaItem[];
    podcasts?: MediaItem[];
    videos?: MediaItem[];
  };
  quickReplies?: QuickReply[];
}

interface GeniusLoci {
  id: string;
  name: string;
  avatar: string;
  voice: string;
  specialties: string[];
  bio: string;
  quote: string;
}

interface Prompt {
  id: string;
  geniusLociId: string;
  initialMessage: string;
  context: string;
  voice: string;
  specialties: string[];
  bio: string;
  quote: string;
  personality?: string;
  avatar?: string;
  guidelines?: Record<string, any>;
}

interface POI {
  id?: string;
  name: string;
  title?: string;
  description: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  question?: string;
  query?: string;
  prompt?: string;
  order?: number;
  orderInPath?: number;
}

const ExperiencesNew = () => {
  const [experiences, setExperiences] = useState<Experience[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<Experience | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [geniusLoci, setGeniusLoci] = useState<Record<string, GeniusLoci>>({});
  const [prompts, setPrompts] = useState<Record<string, Prompt>>({});
  
  // POI management states
  const [pois, setPois] = useState<POI[]>([]);
  const [selectedPOI, setSelectedPOI] = useState<POI | null>(null);
  const [isEditingPOI, setIsEditingPOI] = useState(false);
  const [poiFormData, setPoiFormData] = useState<POI>({
    name: '',
    description: '',
    coordinates: { latitude: 0, longitude: 0 },
    question: ''
  });
  const [formData, setFormData] = useState<Omit<Experience, 'id'>>({
    title: '',
    location: '',
    description: '',
    image: '',
    geniusLociId: '',
    promptId: '',
    tags: [],
    category: '',
    duration: '',
    difficulty: '',
    coordinates: {
      latitude: 0,
      longitude: 0
    },
    media: {
      images: [],
      podcasts: [],
      videos: []
    },
    quickReplies: []
  });

  // Dati hardcoded dall'app mobile (da sincronizzare)
  const mobileExperiences: Experience[] = [
    {
      id: '1',
      title: 'Qui scorre la storia',
      location: 'Lavatoio Pubblico',
      description: 'Nel cuore del borgo, la pietra parla. Tra vasche, campanili e vicoli, scopri frammenti di un passato che resiste. Le chiese custodiscono memorie, le piazze raccontano rivolte, i muri portano versi e segni. Una passeggiata breve, ma intensa, tra tracce di santi, monaci e antichi padroni di feudo. Ogni passo è un ritorno, ogni sosta è una storia.',
      image: '/placeholder-experience.jpg',
      geniusLociId: 'lavatoio',
      promptId: '1',
      tags: [
        { id: 'storia', name: 'Storia', icon: 'book', color: '#52B848' },
        { id: 'cultura', name: 'Cultura', icon: 'color-palette', color: '#F7941D' },
        { id: 'cilento', name: 'Cilento', icon: 'leaf', color: '#52B848' },
        { id: 'genius_loci', name: 'Genius Loci', icon: 'chatbubbles', color: '#E8675C' }
      ],
      category: 'Storia e Tradizioni',
      duration: '< 1 ora',
      difficulty: 'Facile',
      coordinates: {
        latitude: 40.1691,
        longitude: 15.1430
      }
    },
    {
      id: '2',
      title: 'Il respiro del vento',
      location: 'Torre di Guardia',
      description: 'Sulle mura antiche, il vento racconta storie di guardie che vegliavano sui confini del regno. Da qui si dominava la valle, si controllavano i passaggi, si difendeva il borgo. Ogni pietra ha una storia, ogni crepa nasconde un segreto.',
      image: '/placeholder-experience.jpg',
      geniusLociId: 'porto_marina',
      promptId: '11',
      tags: [
        { id: 'storia', name: 'Storia', icon: 'book', color: '#52B848' },
        { id: 'architettura', name: 'Architettura', icon: 'business', color: '#E8675C' },
        { id: 'genius_loci', name: 'Genius Loci', icon: 'chatbubbles', color: '#E8675C' }
      ],
      category: 'Storia e Tradizioni',
      duration: '< 1 ora',
      difficulty: 'Facile',
      coordinates: {
        latitude: 40.1685,
        longitude: 15.1415
      }
    },
    {
      id: '3',
      title: 'Le voci del mercato',
      location: 'Piazza del Mercato',
      description: 'Dove un tempo si vendevano grano e storie, oggi risuonano ancora le voci dei commercianti. Qui si scambiavano merci e notizie, si stringevano accordi e amicizie. Il mercato era il cuore pulsante del borgo.',
      image: '/placeholder-experience.jpg',
      geniusLociId: 'piazza_lista',
      promptId: '2',
      tags: [
        { id: 'cultura', name: 'Cultura', icon: 'color-palette', color: '#F7941D' },
        { id: 'tradizioni', name: 'Tradizioni', icon: 'people', color: '#52B848' },
        { id: 'genius_loci', name: 'Genius Loci', icon: 'chatbubbles', color: '#E8675C' }
      ],
      category: 'Cultura e Tradizioni',
      duration: '< 1 ora',
      difficulty: 'Facile',
      coordinates: {
        latitude: 40.1688,
        longitude: 15.1420
      }
    },
    {
      id: '4',
      title: 'Il sussurro delle acque',
      location: 'Fontana Antica',
      description: 'L\'acqua che scorre da secoli racconta segreti di vita e di morte, di gioia e di dolore. Qui si dissetavano i viandanti, si lavavano i panni, si raccoglievano le storie. L\'acqua è memoria liquida del borgo.',
      image: '/placeholder-experience.jpg',
      tags: [
        { id: 'natura', name: 'Natura', icon: 'leaf', color: '#E8675C' },
        { id: 'storia', name: 'Storia', icon: 'book', color: '#52B848' },
        { id: 'genius_loci', name: 'Genius Loci', icon: 'chatbubbles', color: '#E8675C' }
      ],
      category: 'Natura e Storia',
      duration: '< 1 ora',
      difficulty: 'Facile',
      coordinates: {
        latitude: 40.1690,
        longitude: 15.1425
      }
    },
    {
      id: '5',
      title: 'I colori del tramonto',
      location: 'Belvedere Panoramico',
      description: 'Da qui il sole si tuffa nel mare, dipingendo il cielo con i colori del Cilento. Ogni tramonto è unico, ogni sera è una poesia. Il paesaggio si trasforma in un quadro vivente.',
      image: '/placeholder-experience.jpg',
      tags: [
        { id: 'natura', name: 'Natura', icon: 'leaf', color: '#E8675C' },
        { id: 'panorami', name: 'Panorami', icon: 'camera', color: '#00A0DC' },
        { id: 'genius_loci', name: 'Genius Loci', icon: 'chatbubbles', color: '#E8675C' }
      ],
      category: 'Natura e Paesaggio',
      duration: '30 min',
      difficulty: 'Facile',
      coordinates: {
        latitude: 40.1680,
        longitude: 15.1400
      }
    }
  ];

  const availableTags = [
    { id: 'storia', name: 'Storia', icon: 'book', color: '#52B848' },
    { id: 'natura', name: 'Natura', icon: 'leaf', color: '#E8675C' },
    { id: 'cultura', name: 'Cultura', icon: 'color-palette', color: '#F7941D' },
    { id: 'enogastronomia', name: 'Enogastronomia', icon: 'wine', color: '#00A0DC' },
    { id: 'arte', name: 'Arte', icon: 'brush', color: '#52B848' },
    { id: 'architettura', name: 'Architettura', icon: 'business', color: '#E8675C' },
    { id: 'religione', name: 'Religione', icon: 'add', color: '#F7941D' },
    { id: 'sport', name: 'Sport', icon: 'bicycle', color: '#00A0DC' },
    { id: 'tradizioni', name: 'Tradizioni', icon: 'people', color: '#52B848' },
    { id: 'benessere', name: 'Benessere', icon: 'water', color: '#E8675C' },
    { id: 'artigianato', name: 'Artigianato', icon: 'hammer', color: '#F7941D' },
    { id: 'mito', name: 'Mito', icon: 'planet-outline', color: '#00A0DC' },
    { id: 'mediterraneo', name: 'Mediterraneo', icon: 'boat', color: '#00A0DC' },
    { id: 'cilento', name: 'Cilento', icon: 'leaf', color: '#52B848' },
    { id: 'dieta_mediterranea', name: 'Dieta Mediterranea', icon: 'restaurant', color: '#F7941D' },
    { id: 'arte_contemporanea', name: 'Arte Contemporanea', icon: 'color-palette', color: '#E8675C' },
    { id: 'genius_loci', name: 'Genius Loci', icon: 'chatbubbles', color: '#E8675C' },
    { id: 'civiltà_contadina', name: 'Civiltà Contadina', icon: 'flower', color: '#52B848' },
    { id: 'panorami', name: 'Panorami', icon: 'camera', color: '#00A0DC' },
    { id: 'produttori_locali', name: 'Produttori Locali', icon: 'basket', color: '#F7941D' },
    { id: 'famiglie', name: 'Per Famiglie', icon: 'people-circle', color: '#E8675C' },
    { id: 'turisti', name: 'Per Turisti', icon: 'airplane', color: '#52B848' },
    { id: 'residenti', name: 'Per Residenti', icon: 'home', color: '#00A0DC' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      logDebug('🔄 Iniziando caricamento dati...');
      
      // Carica esperienze
      logDebug('📚 Caricando esperienze...');
      const experiencesCol = collection(db, 'experiences');
      const experiencesSnapshot = await getDocs(experiencesCol);
      const experiencesData = experiencesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Experience[];
      
      logDebug(`📚 Trovate ${experiencesData.length} esperienze in Firestore`);
      
      // Se non ci sono esperienze nel database, usa quelle hardcoded
      if (experiencesData.length === 0) {
        logDebug('⚠️ Nessuna esperienza in Firestore, usando dati hardcoded');
        setExperiences(mobileExperiences);
      } else {
        logDebug('✅ Esperienze caricate da Firestore');
        setExperiences(experiencesData);
      }

      // Carica genius loci
      logDebug('👥 Caricando genius loci...');
      try {
        const geniusLociCol = collection(db, 'geniusLoci');
        const geniusLociSnapshot = await getDocs(geniusLociCol);
        const geniusLociData: Record<string, GeniusLoci> = {};
        geniusLociSnapshot.docs.forEach(doc => {
          geniusLociData[doc.id] = {
            id: doc.id,
            ...doc.data()
          } as GeniusLoci;
        });
        logDebug(`👥 Trovati ${Object.keys(geniusLociData).length} genius loci in Firestore`);
        setGeniusLoci(geniusLociData);
      } catch (error) {
        console.error('❌ Error loading genius loci:', error);
      }

      // Carica prompt
      logDebug('💬 Caricando prompt...');
      try {
        const promptsData = await getPromptsFromFirestore();
        logDebug(`💬 Trovati ${Object.keys(promptsData).length} prompt in Firestore`);
        setPrompts(promptsData);
      } catch (error) {
        console.error('❌ Error loading prompts:', error);
      }
      
      logDebug('✅ Caricamento dati completato');
    } catch (error) {
      console.error('❌ Error loading data:', error);
      // Fallback alle esperienze hardcoded
      setExperiences(mobileExperiences);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectExperience = async (experience: Experience) => {
    setSelectedExperience(experience);
    setFormData({
      title: experience.title,
      location: experience.location,
      description: experience.description,
      image: experience.image,
      geniusLociId: experience.geniusLociId || '',
      promptId: experience.promptId || '',
      tags: experience.tags,
      category: experience.category,
      duration: experience.duration,
      difficulty: experience.difficulty,
      coordinates: experience.coordinates,
      media: experience.media || { images: [], podcasts: [], videos: [] },
      quickReplies: experience.quickReplies || []
    });
    setIsEditing(false);
    
    // Carica i POI per questa esperienza
    await loadPOIs(experience.id);
  };

  const loadPOIs = async (experienceId: string) => {
    try {
      const poisRef = collection(db, 'experiences', experienceId, 'pois');
      const snapshot = await getDocs(poisRef);
      const loadedPOIs: POI[] = [];
      
      snapshot.forEach((doc) => {
        loadedPOIs.push({
          id: doc.id,
          ...doc.data()
        } as POI);
      });
      
      setPois(loadedPOIs);
      logDebug(`✅ POI caricati: ${loadedPOIs.length}`);
    } catch (error) {
      console.error('❌ Errore nel caricamento dei POI:', error);
      setPois([]);
    }
  };

  const handleSavePOI = async () => {
    if (!selectedExperience?.id) return;
    
    try {
      const poiData = {
        name: poiFormData.name,
        title: poiFormData.name,
        description: poiFormData.description,
        coordinates: poiFormData.coordinates,
        question: poiFormData.question || `Raccontami di ${poiFormData.name}`,
        query: poiFormData.question || poiFormData.query,
        prompt: poiFormData.prompt || poiFormData.question,
      };

      if (selectedPOI?.id) {
        // Update existing POI
        const poiRef = doc(db, 'experiences', selectedExperience.id, 'pois', selectedPOI.id);
        await updateDoc(poiRef, poiData);
        logDebug('✅ POI aggiornato');
      } else {
        // Create new POI
        const poisRef = collection(db, 'experiences', selectedExperience.id, 'pois');
        await addDoc(poisRef, poiData);
        logDebug('✅ POI creato');
      }
      
      // Reload POIs
      await loadPOIs(selectedExperience.id);
      
      // Reset form
      setSelectedPOI(null);
      setIsEditingPOI(false);
      setPoiFormData({
        name: '',
        description: '',
        coordinates: { latitude: 0, longitude: 0 },
        question: ''
      });
    } catch (error) {
      console.error('❌ Errore nel salvataggio del POI:', error);
    }
  };

  const handleDeletePOI = async (poiId: string) => {
    if (!selectedExperience?.id) return;
    
    if (!confirm('Sei sicuro di voler eliminare questo POI?')) return;
    
    try {
      const poiRef = doc(db, 'experiences', selectedExperience.id, 'pois', poiId);
      await deleteDoc(poiRef);
      logDebug('✅ POI eliminato');
      
      // Reload POIs
      await loadPOIs(selectedExperience.id);
    } catch (error) {
      console.error('❌ Errore nell\'eliminazione del POI:', error);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleNew = () => {
    setSelectedExperience(null);
    setFormData({
      title: '',
      location: '',
      description: '',
      image: '',
      geniusLociId: '',
      promptId: '',
      tags: [],
      category: '',
      duration: '',
      difficulty: '',
      coordinates: {
        latitude: 0,
        longitude: 0
      },
      media: {
        images: [],
        podcasts: [],
        videos: []
      },
      quickReplies: []
    });
    setIsEditing(true);
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsUploading(true);
      const imagePath = generateImagePath('experiences', selectedExperience?.id || 'new', file.name);
      const imageUrl = await uploadImage(file, imagePath);
      setFormData({ ...formData, image: imageUrl });
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Errore durante il caricamento dell\'immagine');
    } finally {
      setIsUploading(false);
    }
  };

  const handleGenerateQuickReplies = async () => {
    if (!formData.title || !formData.location || !formData.description) {
      alert('Compila almeno titolo, località e descrizione per generare le quick replies');
      return;
    }

    try {
      setIsUploading(true);
      const quickReplies = await generateQuickReplies(
        formData.title,
        formData.location,
        formData.description
      );
      setFormData({ ...formData, quickReplies });
      alert(`✅ Generate ${quickReplies.length} quick replies!`);
    } catch (error) {
      console.error('Error generating quick replies:', error);
      alert('Errore durante la generazione delle quick replies');
    } finally {
      setIsUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      logDebug('💾 [ExperiencesNew] Inizio salvataggio esperienza');
      logDebug('📋 [ExperiencesNew] formData prima della pulizia:', {
        promptId: formData.promptId,
        geniusLociId: formData.geniusLociId,
        title: formData.title
      });
      
      // Pulisci i dati prima del salvataggio
      const dataToSave: any = { ...formData };
      
      // Rimuovi media vuoti o non necessari
      if (dataToSave.media) {
        const hasMedia = 
          (dataToSave.media.images && dataToSave.media.images.length > 0) ||
          (dataToSave.media.podcasts && dataToSave.media.podcasts.length > 0) ||
          (dataToSave.media.videos && dataToSave.media.videos.length > 0);
        
        if (!hasMedia) {
          delete dataToSave.media;
        }
      }
      
      // Rimuovi solo campi undefined (mantieni stringhe vuote per promptId e geniusLociId)
      Object.keys(dataToSave).forEach(key => {
        if (dataToSave[key] === undefined) {
          delete dataToSave[key];
        }
        // Rimuovi stringhe vuote solo per campi non critici
        if (dataToSave[key] === '' && !['promptId', 'geniusLociId'].includes(key)) {
          delete dataToSave[key];
        }
      });
      
      logDebug('📦 [ExperiencesNew] Dati dopo la pulizia:', {
        promptId: dataToSave.promptId,
        geniusLociId: dataToSave.geniusLociId,
        title: dataToSave.title,
        allKeys: Object.keys(dataToSave)
      });

      if (selectedExperience) {
        logDebug('📝 [ExperiencesNew] Aggiornamento esperienza esistente:', selectedExperience.id);
        await updateDoc(doc(db, 'experiences', selectedExperience.id), dataToSave);
        // Aggiorna la lista locale
        setExperiences(prev => prev.map(e => e.id === selectedExperience.id ? { ...e, ...dataToSave } : e));
        setSelectedExperience({ ...selectedExperience, ...dataToSave });
        logDebug('✅ [ExperiencesNew] Esperienza aggiornata con successo');
      } else {
        logDebug('➕ [ExperiencesNew] Creazione nuova esperienza');
        const newExperience = await addDoc(collection(db, 'experiences'), dataToSave);
        const newExp = { id: newExperience.id, ...dataToSave };
        setExperiences(prev => [...prev, newExp]);
        setSelectedExperience(newExp);
        logDebug('✅ [ExperiencesNew] Nuova esperienza creata con ID:', newExperience.id);
      }
      setIsEditing(false);
      alert('Esperienza salvata con successo!');
    } catch (error) {
      console.error('Error saving experience:', error);
      alert(`Errore durante il salvataggio: ${error}`);
    }
  };

  const handleCancel = () => {
    if (selectedExperience) {
      setFormData({
        title: selectedExperience.title,
        location: selectedExperience.location,
        description: selectedExperience.description,
        image: selectedExperience.image,
        geniusLociId: selectedExperience.geniusLociId || '',
        promptId: selectedExperience.promptId || '',
        tags: selectedExperience.tags,
        category: selectedExperience.category,
        duration: selectedExperience.duration,
        difficulty: selectedExperience.difficulty,
        coordinates: selectedExperience.coordinates,
        media: selectedExperience.media || { images: [], podcasts: [], videos: [] },
        quickReplies: selectedExperience.quickReplies || []
      });
    } else {
      setFormData({
        title: '',
        location: '',
        description: '',
        image: '',
        geniusLociId: '',
        promptId: '',
        tags: [],
        category: '',
        duration: '',
        difficulty: '',
        coordinates: {
          latitude: 0,
          longitude: 0
        },
        media: {
          images: [],
          podcasts: [],
          videos: []
        },
        quickReplies: []
      });
    }
    setIsEditing(false);
  };



  const handleDelete = async (experienceId: string) => {
    if (window.confirm('Sei sicuro di voler eliminare questa esperienza?')) {
      try {
        await deleteDoc(doc(db, 'experiences', experienceId));
        setExperiences(prev => prev.filter(e => e.id !== experienceId));
        if (selectedExperience?.id === experienceId) {
          setSelectedExperience(null);
        }
      } catch (error) {
        console.error('Error deleting experience:', error);
      }
    }
  };


  const toggleTag = (tag: typeof availableTags[0]) => {
    if (formData.tags.some(t => t.id === tag.id)) {
      setFormData({
        ...formData,
        tags: formData.tags.filter(t => t.id !== tag.id)
      });
    } else {
      setFormData({
        ...formData,
        tags: [...formData.tags, tag]
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-primary text-white p-6">
        <div className="container-max">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <MapPin size={24} />
              <h1 className="text-2xl font-bold">Gestione Esperienze</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={handleNew}
                className="bg-white/20 hover:bg-white/30 text-white px-4 py-2 rounded-lg transition-colors duration-200 flex items-center space-x-2"
              >
                <Plus size={16} />
                <span>Nuova Esperienza</span>
              </button>
            </div>
          </div>
          <p className="text-white/80 mt-2">
            Gestisci le esperienze dell'app mobile T-Alento
          </p>
          
        </div>
      </div>

      <div className="container-max p-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista Esperienze */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              <div className="p-4 border-b border-border">
                <h2 className="text-lg font-semibold text-text-primary">Esperienze Disponibili</h2>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {isLoading ? (
                  <div className="p-4 space-y-3">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="flex items-center space-x-3 p-3 animate-pulse">
                        <div className="w-16 h-16 bg-gray-200 rounded-lg"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded mb-2"></div>
                          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-2">
                    {experiences.map((experience) => (
                      <div
                        key={experience.id}
                        onClick={() => handleSelectExperience(experience)}
                        className={`flex items-center space-x-3 p-3 rounded-lg cursor-pointer transition-colors duration-200 ${
                          selectedExperience?.id === experience.id
                            ? 'bg-primary/10 border border-primary/20'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={experience.image || '/placeholder-experience.jpg'} 
                            alt={experience.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-text-primary truncate">
                            {experience.title}
                          </h3>
                          <p className="text-sm text-text-secondary truncate">
                            {experience.location}
                          </p>
                          <div className="flex items-center space-x-2 mt-1">
                            <Clock size={12} className="text-text-secondary" />
                            <span className="text-xs text-text-secondary">{experience.duration}</span>
                            <TrendingUp size={12} className="text-text-secondary" />
                            <span className="text-xs text-text-secondary">{experience.difficulty}</span>
                          </div>
                        </div>
                        <div className="flex space-x-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectExperience(experience);
                              handleEdit();
                            }}
                            className="p-1 text-text-secondary hover:text-primary transition-colors duration-200"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(experience.id);
                            }}
                            className="p-1 text-text-secondary hover:text-error transition-colors duration-200"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Form di Editing */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-border">
              {selectedExperience || isEditing ? (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                      {selectedExperience && (
                        <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={selectedExperience.image || '/placeholder-experience.jpg'} 
                            alt={selectedExperience.title}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      <div>
                        <h2 className="text-xl font-semibold text-text-primary">
                          {selectedExperience ? selectedExperience.title : 'Nuova Esperienza'}
                        </h2>
                        {selectedExperience && (
                          <p className="text-text-secondary">
                            {selectedExperience.location}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={handleSave}
                            className="flex items-center space-x-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 transition-colors duration-200"
                          >
                            <Save size={16} />
                            <span>Salva</span>
                          </button>
                          <button
                            onClick={handleCancel}
                            className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors duration-200"
                          >
                            <X size={16} />
                            <span>Annulla</span>
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={handleEdit}
                          className="flex items-center space-x-2 bg-secondary text-white px-4 py-2 rounded-lg hover:bg-secondary/90 transition-colors duration-200"
                        >
                          <Edit size={16} />
                          <span>Modifica</span>
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-6">
                    {/* Informazioni Base */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Titolo *
                        </label>
                        <input
                          type="text"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Luogo *
                        </label>
                        <input
                          type="text"
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Descrizione *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        disabled={!isEditing}
                        rows={4}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                      />
                    </div>

                    {/* Immagine */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Immagine
                      </label>
                      <div className="flex items-center space-x-4">
                        <div className="w-24 h-24 bg-gray-200 rounded-lg overflow-hidden">
                          <img 
                            src={formData.image || '/placeholder-experience.jpg'} 
                            alt="Preview"
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="flex-1">
                          <input
                            type="url"
                            value={formData.image}
                            onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                            disabled={!isEditing}
                            className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                            placeholder="URL dell'immagine"
                          />
                          {isEditing && (
                            <div className="mt-2">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageUpload}
                                className="hidden"
                                id="image-upload"
                                disabled={isUploading}
                              />
                              <label
                                htmlFor="image-upload"
                                className="flex items-center space-x-2 text-primary hover:text-secondary transition-colors duration-200 cursor-pointer"
                              >
                                <Upload size={16} />
                                <span className="text-sm">
                                  {isUploading ? 'Caricamento...' : 'Carica immagine'}
                                </span>
                              </label>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Prompt (include automaticamente il Genius Loci) */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        <MessageCircle size={16} className="inline mr-2" />
                        Prompt & Genius Loci
                      </label>
                      <select
                        value={formData.promptId || ''}
                        onChange={(e) => {
                          const selectedPrompt = prompts[e.target.value];
                          setFormData({ 
                            ...formData, 
                            promptId: e.target.value,
                            geniusLociId: selectedPrompt?.geniusLociId || ''
                          });
                        }}
                        disabled={!isEditing}
                        className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                      >
                        <option value="">Seleziona un Prompt</option>
                        {Object.values(prompts).map((prompt) => {
                          const geniusName = geniusLoci[prompt.geniusLociId]?.name || prompt.geniusLociId;
                          const geniusBio = geniusLoci[prompt.geniusLociId]?.bio || '';
                          return (
                            <option key={prompt.id} value={prompt.id}>
                              {geniusName} - {geniusBio.substring(0, 60)}
                            </option>
                          );
                        })}
                      </select>
                      {formData.promptId && prompts[formData.promptId] && (
                        <div className="mt-2 p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-2">
                            <User size={14} className="inline mr-1" />
                            <strong>Genius Loci:</strong> {geniusLoci[prompts[formData.promptId].geniusLociId]?.name || prompts[formData.promptId].geniusLociId}
                          </p>
                          <p className="text-sm text-gray-600">
                            <strong>Messaggio iniziale:</strong> {prompts[formData.promptId].initialMessage}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            <strong>Contesto:</strong> {prompts[formData.promptId].context}
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Categoria, Durata, Difficoltà */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Categoria *
                        </label>
                        <input
                          type="text"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Durata *
                        </label>
                        <input
                          type="text"
                          value={formData.duration}
                          onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          Difficoltà *
                        </label>
                        <input
                          type="text"
                          value={formData.difficulty}
                          onChange={(e) => setFormData({ ...formData, difficulty: e.target.value })}
                          disabled={!isEditing}
                          className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary disabled:bg-gray-50 disabled:text-gray-500"
                        />
                      </div>
                    </div>

                    {/* Coordinate con Google Maps */}
                    <MapCoordinatePicker
                      latitude={formData.coordinates.latitude}
                      longitude={formData.coordinates.longitude}
                      onCoordinatesChange={(lat, lng) => setFormData({
                        ...formData,
                        coordinates: { latitude: lat, longitude: lng }
                      })}
                      label="Coordinate Esperienza"
                    />

                    {/* Tags */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        Tag
                      </label>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {formData.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 text-sm rounded-full flex items-center space-x-2"
                            style={{ 
                              backgroundColor: tag.color + '20', 
                              color: tag.color 
                            }}
                          >
                            <span>{tag.name}</span>
                            {isEditing && (
                              <button
                                onClick={() => toggleTag(tag)}
                                className="text-current hover:opacity-70"
                              >
                                ✕
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                      {isEditing && (
                        <div className="flex flex-wrap gap-2">
                          {availableTags.filter(tag => !formData.tags.some(t => t.id === tag.id)).map((tag) => (
                            <button
                              key={tag.id}
                              onClick={() => toggleTag(tag)}
                              className="px-3 py-1 text-sm border border-border rounded-full hover:bg-gray-50 transition-colors duration-200"
                            >
                              {tag.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Gestione Media - Solo per esperienze esistenti */}
                    {selectedExperience && selectedExperience.id && (
                      <ExperienceMediaManager
                        experienceId={selectedExperience.id}
                        media={formData.media || { images: [], podcasts: [], videos: [] }}
                        onMediaUpdate={(updatedMedia) => setFormData({ ...formData, media: updatedMedia })}
                      />
                    )}

                    {/* Gestione POI - Solo per esperienze esistenti */}
                    {selectedExperience && selectedExperience.id && (
                      <div className="border-t border-gray-200 pt-6 mt-6">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold text-gray-800">
                            <MapPin size={20} className="inline mr-2" />
                            Punti di Interesse (POI)
                          </h3>
                          <button
                            type="button"
                            onClick={() => {
                              setPoiFormData({
                                name: '',
                                description: '',
                                coordinates: { latitude: 0, longitude: 0 },
                                question: ''
                              });
                              setIsEditingPOI(true);
                            }}
                            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors flex items-center space-x-2"
                          >
                            <Plus size={16} />
                            <span>Aggiungi POI</span>
                          </button>
                        </div>
                        
                        {/* Lista POI esistenti */}
                        {pois.length > 0 ? (
                          <div className="space-y-3">
                            {pois.map((poi) => (
                              <div key={poi.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                                <div className="flex-1">
                                  <h4 className="font-medium text-gray-800">{poi.name}</h4>
                                  <p className="text-sm text-gray-600">{poi.description}</p>
                                  <p className="text-xs text-gray-500 mt-1">
                                    Coordinate: {poi.coordinates.latitude.toFixed(6)}, {poi.coordinates.longitude.toFixed(6)}
                                  </p>
                                  {poi.question && (
                                    <p className="text-xs text-primary mt-1">
                                      Domanda: {poi.question}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => {
                                      setSelectedPOI(poi);
                                      setPoiFormData(poi);
                                      setIsEditingPOI(true);
                                    }}
                                    className="p-2 text-gray-600 hover:text-primary transition-colors"
                                  >
                                    <Edit size={16} />
                                  </button>
                                  <button
                                    onClick={() => handleDeletePOI(poi.id!)}
                                    className="p-2 text-gray-600 hover:text-error transition-colors"
                                  >
                                    <Trash2 size={16} />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="text-center py-8 bg-gray-50 rounded-lg">
                            <MapPin size={32} className="mx-auto text-gray-400 mb-2" />
                            <p className="text-gray-500 text-sm">
                              Nessun POI configurato. Clicca su "Aggiungi POI" per crearne uno.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Quick Replies */}
                    <div className="border-t border-gray-200 pt-6 mt-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                          <MessageCircle size={20} className="inline mr-2" />
                          Quick Replies
                        </h3>
                        <button
                          type="button"
                          onClick={handleGenerateQuickReplies}
                          disabled={!formData.title || !formData.location || !formData.description || isUploading}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
                        >
                          <MessageCircle size={16} />
                          <span>{isUploading ? 'Generazione...' : 'Genera con AI'}</span>
                        </button>
                      </div>
                      
                      {formData.quickReplies && formData.quickReplies.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {formData.quickReplies.map((qr) => (
                            <div
                              key={qr.id}
                              className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-center space-x-3"
                            >
                              <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                                <MessageCircle size={16} className="text-primary" />
                              </div>
                              <p className="text-sm text-gray-700">{qr.text}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-8 bg-gray-50 rounded-lg">
                          <MessageCircle size={32} className="mx-auto text-gray-400 mb-2" />
                          <p className="text-gray-500 text-sm">
                            Nessuna quick reply generata. Clicca su "Genera con AI" per crearle automaticamente.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-12 text-center">
                  <MapPin size={48} className="mx-auto text-gray-400 mb-4" />
                  <h3 className="text-lg font-medium text-text-primary mb-2">
                    Seleziona un'Esperienza
                  </h3>
                  <p className="text-text-secondary">
                    Scegli un'esperienza dalla lista per modificarla o creane una nuova
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal POI */}
      {isEditingPOI && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-gray-800">
                  {selectedPOI ? 'Modifica POI' : 'Nuovo POI'}
                </h3>
                <button
                  onClick={() => {
                    setIsEditingPOI(false);
                    setSelectedPOI(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nome POI *
                  </label>
                  <input
                    type="text"
                    value={poiFormData.name}
                    onChange={(e) => setPoiFormData({ ...poiFormData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Es: Lavatoio Pubblico"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Descrizione *
                  </label>
                  <textarea
                    value={poiFormData.description}
                    onChange={(e) => setPoiFormData({ ...poiFormData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows={3}
                    placeholder="Descrizione del punto di interesse"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Domanda per il Genius Loci
                  </label>
                  <input
                    type="text"
                    value={poiFormData.question || ''}
                    onChange={(e) => setPoiFormData({ ...poiFormData, question: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    placeholder="Es: Raccontami la storia di questo luogo"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Questa domanda verrà inviata al Genius Loci quando l'utente clicca sul POI nella mappa
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Coordinate
                  </label>
                  <MapCoordinatePicker
                    latitude={poiFormData.coordinates.latitude}
                    longitude={poiFormData.coordinates.longitude}
                    onCoordinatesChange={(lat, lng) => setPoiFormData({ 
                      ...poiFormData, 
                      coordinates: { latitude: lat, longitude: lng } 
                    })}
                    label="Clicca sulla mappa per selezionare la posizione del POI"
                  />
                </div>

                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={handleSavePOI}
                    disabled={!poiFormData.name || !poiFormData.description}
                    className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  >
                    <Save size={16} className="inline mr-2" />
                    Salva POI
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingPOI(false);
                      setSelectedPOI(null);
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    Annulla
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperiencesNew;
